import React, { useState, useEffect } from 'react';
import { Menu, X, PawPrint, Search } from 'lucide-react';
import { Button } from './ui/button';
import { SearchModal } from './SearchModal';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    const handleKeyDown = (e) => {
      // Open search with Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'About', id: 'about' },
    { label: 'Services', id: 'services' },
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Partners', id: 'partners' },
    { label: 'Import Checklist', id: null, href: '/import-checklist' },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* Logo */}
          <div 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3 cursor-pointer group flex-shrink-0"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_pet-relocation-hub/artifacts/vmr2h399_Pawsome%20Logo.jpg"
              alt="Pawsome Ventures Logo"
              className="w-12 h-12 object-contain group-hover:scale-110 transition-transform"
            />
            <span className="text-xl font-bold text-foreground whitespace-nowrap hidden sm:inline xl:hidden 2xl:inline">Pawsome Ventures</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
            {navItems.map((item) => (
              item.href ? (
                <a
                  key={item.label}
                  href={item.href}
                  className={`text-sm font-medium whitespace-nowrap transition-colors ${
                    item.label === 'Import Checklist' 
                      ? 'text-emerald-600 hover:text-emerald-700' 
                      : 'text-foreground/80 hover:text-primary'
                  }`}
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm text-foreground/80 hover:text-primary font-medium whitespace-nowrap transition-colors"
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* Desktop Actions - Search, TravelSafe, Client Portal, CTA */}
          <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="flex items-center justify-center w-9 h-9 bg-muted hover:bg-muted/80 rounded-lg transition-colors border border-border group"
              title="Search (Cmd/Ctrl + K)"
              aria-label="Search"
            >
              <Search className="w-4 h-4 text-foreground/60 group-hover:text-primary transition-colors" />
            </button>

            <a
              href="/travelsafe"
              className="px-3 xl:px-3 2xl:px-4 py-2 text-sm bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all shadow-sm whitespace-nowrap"
            >
              TravelSafe
            </a>
            <a
              href="/client/login"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap transition-colors"
            >
              Client Portal
            </a>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-primary hover:bg-primary/90 text-white whitespace-nowrap px-3 2xl:px-4"
            >
              <span className="2xl:hidden">Schedule</span>
              <span className="hidden 2xl:inline">Schedule Consultation</span>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="xl:hidden text-foreground flex-shrink-0"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden py-4 border-t border-border bg-white">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-left text-foreground/80 hover:text-primary font-medium transition-colors px-4 py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-left text-foreground/80 hover:text-primary font-medium transition-colors px-4 py-2"
                  >
                    {item.label}
                  </button>
                )
              ))}
              <a
                href="/travelsafe"
                className="text-left px-4 py-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                TravelSafe
              </a>
              <a
                href="/client/login"
                className="text-left text-blue-600 hover:text-blue-700 font-medium transition-colors px-4 py-2"
              >
                Client Portal
              </a>
              <div className="px-4 pt-2">
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                >
                  Schedule Consultation
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
      
      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
};
